import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Spinner, Modal, Label } from 'flowbite-react';
import {
  HiRefresh,
  HiSearch,
  HiTrash,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';

interface Contact {
  id: string;
  nombre: string;
  empresa: string | null;
  email: string | null;
  telefono: string;
  comentario: string;
  created_at: string;
  updated_at: string;
}

export default function AdminContacts() {
  const { userProfile } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContacts(data || []);
      setFilteredContacts(data || []);
    } catch (error: any) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadContacts();
    }
  }, [userProfile]);

  useEffect(() => {
    let filtered = contacts;
    if (searchTerm) {
      filtered = filtered.filter(
        (contact) =>
          contact.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.empresa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.telefono.includes(searchTerm)
      );
    }
    setFilteredContacts(filtered);
  }, [searchTerm, contacts]);

  const deleteContact = async (contact: Contact) => {
    if (!confirm(`¿Estás seguro de eliminar el contacto "${contact.nombre}"?`)) return;
    
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contact.id);

      if (error) throw error;
      await loadContacts();
    } catch (error: any) {
      console.error('Error deleting contact:', error);
      alert('Error al eliminar contacto: ' + (error.message || 'Error desconocido'));
    }
  };

  const openDetailsModal = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Contactos</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Gestiona los contactos recibidos</p>
          </div>
          <Button color="gray" onClick={loadContacts} className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
            <HiRefresh className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <TextInput
                  type="text"
                  placeholder="Buscar por nombre, empresa, email o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredContacts.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron contactos</p>
            </div>
          </Card>
        ) : (
          <Card className="bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">Nombre</th>
                    <th scope="col" className="px-6 py-3">Empresa</th>
                    <th scope="col" className="px-6 py-3">Email</th>
                    <th scope="col" className="px-6 py-3">Teléfono</th>
                    <th scope="col" className="px-6 py-3">Fecha</th>
                    <th scope="col" className="px-6 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact) => (
                    <tr key={contact.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                      <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">{contact.nombre}</td>
                      <td className="px-6 py-4">{contact.empresa || '-'}</td>
                      <td className="px-6 py-4">{contact.email || '-'}</td>
                      <td className="px-6 py-4">{contact.telefono}</td>
                      <td className="px-6 py-4">
                        {new Date(contact.created_at).toLocaleDateString('es-ES')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openDetailsModal(contact)}
                            size="sm"
                            color="gray"
                            className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            Ver
                          </Button>
                          <Button
                            onClick={() => deleteContact(contact)}
                            size="sm"
                            color="failure"
                            className="gap-1"
                          >
                            <HiTrash className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Modal de Detalles */}
        <Modal show={showDetailsModal} onClose={() => setShowDetailsModal(false)} size="lg">
          <div className="p-6 bg-white dark:bg-gray-800">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Detalles del Contacto</h3>
            {selectedContact && (
              <div className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">{selectedContact.nombre}</p>
                </div>
                {selectedContact.empresa && (
                  <div>
                    <Label>Empresa</Label>
                    <p className="mt-1 text-gray-900 dark:text-white">{selectedContact.empresa}</p>
                  </div>
                )}
                {selectedContact.email && (
                  <div>
                    <Label>Email</Label>
                    <p className="mt-1 text-gray-900 dark:text-white">{selectedContact.email}</p>
                  </div>
                )}
                <div>
                  <Label>Teléfono</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">{selectedContact.telefono}</p>
                </div>
                <div>
                  <Label>Comentario</Label>
                  <p className="mt-1 text-gray-900 dark:text-white whitespace-pre-wrap">{selectedContact.comentario}</p>
                </div>
                <div>
                  <Label>Fecha de Registro</Label>
                  <p className="mt-1 text-gray-900 dark:text-white">
                    {new Date(selectedContact.created_at).toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button color="gray" onClick={() => setShowDetailsModal(false)} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
